import dayjs from 'dayjs';
import _ from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { CommonLink, useCommonNavigate } from '@client/component/CommonLink';
import { NewsSummaryListResponse } from '../../../response/responseSchema';
import List from '@client/uikit/List';
import { apiClient } from '@app/apiClient';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import firebaseStore from '@client/store/firebaseStore';
import modalStore from '@client/store/modalStore';
import { CommonTimeFormat } from '@client/component/CommonDateFormat';

class ViewStore {
    newsSummaryList: NewsSummaryListResponse | null = null;
    lastHash: string | null = null;
    isLoading: boolean = false;

    constructor() {
        makeAutoObservable(this);
    }

    async fetch() {
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;

        const newsSummaryList = await apiClient.news.public.getNewsSummaryList.$query();

        runInAction(() => {
            this.newsSummaryList = newsSummaryList;
            this.lastHash = _.last(this.newsSummaryList.items)?.hash || null;
            this.isLoading = false;
        });
    }

    async fetchMore() {
        if (this.isLoading) {
            return;
        }

        if (this.lastHash === null || this.newsSummaryList?.isEnd) {
            return;
        }

        this.isLoading = true;

        const moreNewsSummaryList = await apiClient.news.member.getNewsSummaryMoreList.$query({
            fromHash: this.lastHash,
        });

        runInAction(() => {
            const prevItems = this.newsSummaryList?.items || [];
            this.newsSummaryList = {
                ...moreNewsSummaryList,
                items: [...prevItems, ...moreNewsSummaryList.items],
            };
            this.lastHash = _.last(this.newsSummaryList.items)?.hash || null;
            this.isLoading = false;
        });
    }
}

const NewsListItem = observer(
    ({ newsListItem }: { newsListItem: NewsSummaryListResponse['items'][number] }) => {
        const { navigate } = useCommonNavigate();

        return (
            <article
                className="list-col-grow cursor-pointer py-1"
                role="button"
                onClick={(e) => {
                    e.preventDefault();
                    navigate(`/news/${newsListItem.slug}`);
                }}
            >
                <header>
                    <CommonLink
                        to={`/news/${newsListItem.slug}`}
                        className="link link-hover block text-lg leading-tight font-semibold tracking-tight"
                        preventScrollReset={false}
                    >
                        {newsListItem.title}
                    </CommonLink>
                </header>
                <div className="text-base-content/70 mt-1 text-sm">
                    {newsListItem.conclusion}
                    <footer className="text-base-content/40 mt-3.5 text-xs">
                        <CommonTimeFormat time={newsListItem.publishedAt} fromNow={true} />
                    </footer>
                </div>
            </article>
        );
    },
);

const NewsList = observer(
    ({
        displayCount,
        isShowMoreButton = true,
        className,
    }: {
        displayCount?: number;
        isShowMoreButton?: boolean;
        className?: string;
    }) => {
        const viewStore = useMobxInstance(ViewStore);

        useEffect(() => {
            viewStore.fetch();
        }, [viewStore]);

        const items = _.isNumber(displayCount)
            ? _.take(viewStore.newsSummaryList?.items, displayCount)
            : viewStore.newsSummaryList?.items;

        const hasMoreItems =
            !viewStore.isLoading &&
            !_.isEmpty(viewStore.newsSummaryList?.items) &&
            !viewStore.newsSummaryList?.isEnd;

        return (
            <section className={className}>
                <List>
                    {_.map(items, (newsListItem) => (
                        <List.Item key={newsListItem.hash}>
                            <NewsListItem newsListItem={newsListItem} />
                        </List.Item>
                    ))}
                </List>
                {isShowMoreButton && hasMoreItems && (
                    <footer className="mt-2">
                        <button
                            className="btn btn-block btn-soft btn-sm font-normal"
                            onClick={async () => {
                                const isLoggedIn = await firebaseStore.isLoggedIn();

                                if (isLoggedIn) {
                                    viewStore.fetchMore();
                                } else {
                                    const { returnValue } = await modalStore.haewao.login();

                                    if (returnValue) {
                                        viewStore.fetchMore();
                                    }
                                }
                            }}
                        >
                            {viewStore.isLoading ? (
                                <span className="loading loading-ring loading-sm"></span>
                            ) : (
                                '더보기'
                            )}
                        </button>
                    </footer>
                )}
            </section>
        );
    },
);

export default NewsList;
